import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import s1 from './s1.png';
import s1_b from './s1_b.png';
import s1_bb from './s1_bb.png';
import s1_c from './s1_c.png';
import s1_cc from './s1_cc.png';
import s1_d from './s1_d.PNG';
import s1_dd from './s1_dd.PNG';
import s1_e from './s1_e.PNG';
import s1_ee from './s1_ee.PNG';
import s2_b from './s2_b.png';
import s2_bb from './s2_bb.png';
import s2_c from './s2_c.png';
import s2_cc from './s2_cc.png';
import s2_d from './s2_d.PNG';
import s2_dd from './s2_dd.PNG';
import s2_e from './s2_e.PNG';
import s2_ee from './s2_ee.PNG';

// The Flask server hosts both the site and the API, so requests go to the same address.
const API_BASE = '';

// How often to ask the server for progress (ms)
const POLL_INTERVAL = 3000;

// True while this tab has a bulk job that is waiting, uploading or running (used to guard navigation)
let bulkJobActive = false;

const LEAVE_WARNING = 'A bulk prediction job is in progress in this tab. If you leave this page, your job will be terminated and no results will be saved. Leave anyway?';

// How long a job is kept, and this tab keeps trying to reconnect, after the connection is lost (ms)
const CONNECTION_GRACE_MS = 10 * 60 * 1000;
const STORAGE_KEY = 'dnmt1pred_active_job';

// Remember this tab's active job in the browser (per tab) so a reload or a connection loss can pick it up again
function saveActiveJob(info) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...info, savedAt: Date.now() })); } catch (e) { /* storage unavailable */ }
}
function loadActiveJob() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    if (saved && saved.jobId && Date.now() - saved.savedAt < CONNECTION_GRACE_MS) return saved;
  } catch (e) { /* nothing saved */ }
  return null;
}
function clearActiveJob() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* storage unavailable */ }
}

// Tell the server to terminate a job (works while the page is closing). With a delay, the server waits that
// many seconds first, and a status check from the same tab in the meantime (a reload) undoes it.
function cancelServerJob(jobId, delaySeconds = 0) {
  try {
    const url = `${API_BASE}/cancel/${jobId}${delaySeconds ? `?delay=${delaySeconds}` : ''}`;
    const sent = navigator.sendBeacon ? navigator.sendBeacon(url) : false;
    if (!sent) axios.post(url).catch(() => {});
  } catch (e) {
    // nothing more can be done if the page is already going away
  }
}

// Shared Nav
function Nav() {
  const guard = (e) => {
    if (bulkJobActive && !window.confirm(LEAVE_WARNING)) e.preventDefault();
  };
  return (
    <nav>
      <Link to="/" onClick={guard}>Home</Link>
      <Link to="/predict" onClick={guard}>Predict</Link>
      <Link to="/tutorial" onClick={guard}>Tutorial</Link>
      <Link to="/faqs" onClick={guard}>FAQs</Link>
      <Link to="/contact" onClick={guard}>Contact</Link>
    </nav>
  );
}

// Home Page
function Home() {
  const navigate = useNavigate();
  const handlePredictClick = () => {
    navigate('/predict');
  };
  return (
    <>
      <Helmet>
        <title>DNMT1pred | Predict DNMT1 Inhibitors with Machine Learning</title>
        <meta name="description" content="Free web tool to predict activity of chemical compounds against DNMT1 using Logistic Regression, XGBoost, SVM, and Gradient Boosting models." />
      </Helmet>
      <div className="container mt-4">
        <header className="d-flex justify-content-between align-items-center">
          <h1>DNMT1pred</h1>
          <Nav />
        </header>
        <div className="home-description">
          <p>
            Welcome to <strong>DNMT1pred</strong>, a web-based machine learning (ML) application designed to predict the activity of chemical compounds against DNMT1 as computationally reasonable effective epigenetic-targeted therapeutic agents. Given the major role this enzyme plays in epigenetic regulation, predicted compounds can be explored experimentally as potential therapeutic agents for various cancers. The web application offers predictions using <strong>Logistic Regression</strong>, <strong>XGBoost</strong>, <strong>Support Vector Machine</strong> and <strong>Gradient Boosting Machine</strong>.
          </p>
          <p>
            After training, testing and validation of models on bioactivity dataset consisting of 3371 training samples, 756 testing samples and 85 validation compounds, all four models achieved competitive metrics for predicting DNMT1 inhibitors with marginal performance differences amongst them. All four models achieved testing accuracy of 91% however <strong>Logistic Regression</strong> and <strong>Gradient Boosting Machine</strong> showed effective robustness and made high confident predictions from within their AD. Molecules submitted for predictions that are highly potential to be classified unreliably are flagged by a soft adapted Linear Regression filter and labeled <strong>Inconclusive</strong>, with their confidence level and applicability domain (AD) reported as <strong>N/A</strong>.
          </p>
        </div>
        <div className="home-predict-button">
          <button className="btn btn-primary" onClick={handlePredictClick}>
            Predict
          </button>
        </div>
      </div>
    </>
  );
}

// Predict Page
function Predict() {
  const [cid, setCid] = useState('');
  const [smiles, setSmiles] = useState('');
  const [file, setFile] = useState(null);
  const [model, setModel] = useState('Logistic Regression');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('input-smiles');
  const [progress, setProgress] = useState(null);
  const [jobNumber, setJobNumber] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const [queueMessage, setQueueMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [offline, setOffline] = useState(false);
  const [notice, setNotice] = useState('');
  const isMounted = useRef(true);
  const activeJobRef = useRef(null);
  const stopRequested = useRef(false);
  const abortRef = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    isMounted.current = true;
    // Warn before the tab is closed or refreshed while a bulk job is active, and terminate the job when the page goes away
    const warnBeforeClosing = (e) => {
      if (activeJobRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    const terminateOnClose = () => {
      // Delayed: a reload resumes the job (its first status check undoes this), a closed tab lets it go
      if (activeJobRef.current) cancelServerJob(activeJobRef.current, 20);
    };
    window.addEventListener('beforeunload', warnBeforeClosing);
    window.addEventListener('pagehide', terminateOnClose);
    return () => {
      isMounted.current = false;
      window.removeEventListener('beforeunload', warnBeforeClosing);
      window.removeEventListener('pagehide', terminateOnClose);
      // Leaving the Predict page (menu link, back button) terminates the job too
      if (activeJobRef.current) {
        cancelServerJob(activeJobRef.current);
        activeJobRef.current = null;
        clearActiveJob();
      }
      bulkJobActive = false;
    };
  }, []);
  useEffect(() => {
    console.log('Smiles state updated:', smiles);
  }, [smiles]);
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size > 100 * 1024 * 1024) {
      setError("File size exceeds 100MB. Please upload a smaller file or split larger files into chunks of .txt format.");
      return;
    }
    setFile(selectedFile);
    setCid('');
    setSmiles('');
    setError('');
  };
  const resetBulkState = () => {
    setLoading(false);
    setProgress(null);
    setWaiting(false);
    setUploading(false);
    setOffline(false);
    setQueueMessage('');
    setJobNumber(null);
  };

  // Follow one bulk job (by its own id) until it finishes. jobFile is null when the page was reloaded,
  // because the browser cannot keep the file.
  const followJob = async (jobId, jobFile) => {
    stopRequested.current = false;
    let lastContact = Date.now();
    let uploadStarted = false;
    try {
      while (isMounted.current && !stopRequested.current) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
        if (!isMounted.current || stopRequested.current) return;
        let job;
        try {
          const statusResponse = await axios.post(`${API_BASE}/status/${jobId}`);
          job = statusResponse.data;
          lastContact = Date.now();
          setOffline(false);
          saveActiveJob({ jobId, jobNumber: job.job_number, model: job.model_used });
        } catch (pollErr) {
          console.error('Polling error:', pollErr.message, pollErr.response?.status, pollErr.response?.data);
          if (pollErr.response?.status === 404) {
            setError('The prediction job was not found. The server may have restarted, please resubmit your file.');
            return;
          }
          // Connection trouble: keep trying for up to 10 minutes; the server keeps the job for as long
          if (Date.now() - lastContact > CONNECTION_GRACE_MS) throw pollErr;
          setOffline(true);
          continue;
        }
        if (!isMounted.current || stopRequested.current) return;
        setJobNumber(job.job_number);
        setWaiting(job.status === 'queued');
        setQueueMessage(job.status === 'queued' ? (job.queue_message || '') : '');
        if (job.status === 'running') {
          setProgress({ done: job.done, total: job.total });
        }
        if (job.status === 'error') {
          setError(job.error || 'Error making prediction. Please check your input, uploaded file, or server status.');
          activeJobRef.current = null;
          return;
        }
        if ((job.status === 'queued' || job.status === 'ready' || job.status === 'uploading') && !jobFile) {
          setError('This page was reloaded before your job started, so the file is no longer available. Your job was cancelled, please select your file and submit again.');
          return;
        }
        if (job.status === 'ready' && !uploadStarted) {
          // Its turn has come: only now is the file sent to the server
          uploadStarted = true;
          setUploading(true);
          setUploadPercent(0);
          // Tell the server the upload is starting, so a long upload is never mistaken for a missing file
          await axios.post(`${API_BASE}/claim/${jobId}`);
          abortRef.current = new AbortController();
          const uploadForm = new FormData();
          uploadForm.append('file', jobFile);
          // Keep checking in while the file is being sent (the loop is busy waiting for the upload)
          const heartbeat = setInterval(() => { axios.post(`${API_BASE}/status/${jobId}`).catch(() => {}); }, 5000);
          let uploadResponse;
          try {
            uploadResponse = await axios.post(`${API_BASE}/upload/${jobId}`, uploadForm, {
              headers: { 'Content-Type': 'multipart/form-data' },
              signal: abortRef.current.signal,
              onUploadProgress: (ev) => { if (ev.total) setUploadPercent(Math.round((ev.loaded * 100) / ev.total)); },
            });
          } finally {
            clearInterval(heartbeat);
          }
          if (!isMounted.current || stopRequested.current) return;
          setUploading(false);
          setProgress({ done: 0, total: uploadResponse.data.total });
        }
        if (job.status === 'done') {
          activeJobRef.current = null;
          bulkJobActive = false;
          clearActiveJob();
          navigate('/results', {
            state: {
              bulk: {
                summary: job.summary,
                preview: job.preview,
                model_used: job.model_used,
                download_url: `${API_BASE}${job.download_url}`,
              },
            },
          });
          return;
        }
      }
    } catch (err) {
      if (stopRequested.current) return;
      console.error("Error response:", err.response);
      if (err.response) {
        setError(err.response.data?.message || `Error making prediction (${err.message}). Please check your input, uploaded file, or server status.`);
      } else {
        setError(`The connection to the server was lost for more than 10 minutes (${err.message}). Your job has been terminated and no results were saved. Please check your connection and resubmit your file.`);
      }
    } finally {
      // Whatever ended the loop, make sure the server does not keep working for a tab that stopped listening
      if (activeJobRef.current === jobId) {
        cancelServerJob(jobId);
      }
      activeJobRef.current = null;
      bulkJobActive = false;
      clearActiveJob();
      if (isMounted.current) resetBulkState();
    }
  };

  // Stop button: terminate the queued, uploading or running job of this tab
  const handleStop = async () => {
    if (!window.confirm('Stop this job? It will be terminated and no results will be saved.')) return;
    stopRequested.current = true;
    if (abortRef.current) abortRef.current.abort();
    const jobId = activeJobRef.current;
    activeJobRef.current = null;
    bulkJobActive = false;
    clearActiveJob();
    if (jobId) {
      try { await axios.post(`${API_BASE}/cancel/${jobId}`); } catch (e) { /* the server also gives up on its own after a while */ }
    }
    if (isMounted.current) {
      resetBulkState();
      setNotice('The job was stopped. No results were saved.');
    }
  };

  // After a reload, pick up the job this tab was following (kept in the browser for up to 10 minutes)
  useEffect(() => {
    const saved = loadActiveJob();
    if (saved) {
      activeJobRef.current = saved.jobId;
      bulkJobActive = true;
      setActiveTab('upload-file');
      if (saved.model) setModel(saved.model);
      setJobNumber(saved.jobNumber);
      setLoading(true);
      followJob(saved.jobId, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNotice('');
    // Single SMILES prediction
    if (activeTab === 'input-smiles') {
      if (!smiles) {
        setError('Please provide a SMILES string.');
        setLoading(false);
        return;
      }
      const formData = new FormData();
      formData.append('model', model);
      formData.append('cid', cid);
      formData.append('smiles', smiles);
      try {
        const response = await axios.post(`${API_BASE}/predict`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        navigate('/results', { state: response.data });
      } catch (err) {
        console.error("Error response:", err.response);
        setError(err.response?.data?.message || `Error making prediction (${err.message}). Please check your input, uploaded file, or server status.`);
      } finally {
        setLoading(false);
      }
      return;
    }
    // Bulk file prediction. The tab first takes a place in the line WITHOUT sending the file. The file is
    // uploaded only when the server says it is this job's turn, so waiting jobs never load anything on the server.
    if (activeTab === 'upload-file' && file) {
      try {
        const ticketForm = new FormData();
        ticketForm.append('model', model);
        ticketForm.append('filename', file.name);
        ticketForm.append('size', file.size);
        const ticketResponse = await axios.post(`${API_BASE}/queue`, ticketForm);
        const jobId = ticketResponse.data.job_id;
        activeJobRef.current = jobId;
        bulkJobActive = true;
        saveActiveJob({ jobId, jobNumber: ticketResponse.data.job_number, model });
        setJobNumber(ticketResponse.data.job_number);
        setWaiting(ticketResponse.data.status === 'queued');
        setQueueMessage(ticketResponse.data.queue_message || '');
        setProgress(null);
        await followJob(jobId, file);
      } catch (err) {
        console.error("Error response:", err.response);
        setError(err.response?.data?.message || `Error making prediction (${err.message}). Please check your input, uploaded file, or server status.`);
        resetBulkState();
      }
      return;
    }
    setError('Please provide a SMILES string or upload a file.');
    setLoading(false);
  };
  const useExampleMolecule = () => {
    setCid('1');
    setSmiles('COc1ccc2c(c1)[nH]c1c(C)c(OS(=O)(=O)c3cccc4c(N(C)C)cccc34)ccc12');
    setFile(null);
    setActiveTab('input-smiles');
  };
  const downloadExampleFile = () => {
    const exampleContent = "ID\tSMILES\n" +
      "1\tO=C(NCCN1CCCC1)c1cc(-c2ccncc2)nc2ccc(S(=O)(=O)N3CCOCC3)cc12\n" +
      "2\tO=C1OCCC1NC1=NCCC1\n" +
      "3\tCN(C)[C@@H]1C(=O)C(C(N)=O)=C(O)[C@@]2(O)C(=O)C3=C(O)c4c(O)cccc4[C@@](C)(O)[C@H]3[C@H](O)[C@@H]12\n";
    const blob = new Blob([exampleContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample_file_for_upload.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <Helmet>
        <title>DNMT1pred | Make Predictions</title>
        <meta name="description" content="Predict DNMT1 inhibitor activity for single molecules or upload bulk files using Logistic Regression, XGBoost, SVM, and GBM models." />
      </Helmet>
      <div className="container mt-4">
        <header className="d-flex justify-content-between align-items-center">
          <h1>DNMT1pred</h1>
          <Nav />
        </header>
        <div className="row mt-4 justify-content-center">
          <div className="col-md-6">
            <div className="card mt-2 predict-form">
              <div className="card-body">
                <ul className="nav nav-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'input-smiles' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('input-smiles');
                        setFile(null);
                      }}
                    >
                      Input Single SMILES
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'upload-file' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('upload-file');
                        setCid('');
                        setSmiles('');
                      }}
                    >
                      Upload Bulk File
                    </button>
                  </li>
                </ul>
                {activeTab === 'input-smiles' ? (
                  <>
                    <p>Enter a single molecule ID (optional but recommended) and SMILES to make a single molecule prediction.</p>
                    <button className="btn btn-link" onClick={useExampleMolecule}>
                      Use example molecule
                    </button>
                  </>
                ) : (
                  <>
                    <p>Upload a <strong>tab-separated text file</strong> containing molecule ID (optional but recommended) and SMILES to make prediction for multiple compounds.</p>
                    <p>Please ensure uploaded file does <strong>not exceed 100MB</strong>, otherwise consider splitting it into chunks of not more than 100MB for upload.</p>
                    <p><strong>File must have "ID" and "SMILES" as headers</strong>. Sample file for upload can be downloaded below</p>
                    <button className="btn btn-link" onClick={downloadExampleFile}>
                      Download Sample File for Upload
                    </button>
                  </>
                )}
                <form onSubmit={handleSubmit}>
                  {activeTab === 'input-smiles' && (
                    <>
                      <div className="mb-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Molecule ID"
                          value={cid}
                          onChange={(e) => setCid(e.target.value)}
                        />
                      </div>
                      <div className="mb-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="SMILES"
                          value={smiles}
                          onChange={(e) => setSmiles(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                  {activeTab === 'upload-file' && (
                    <div className="mb-3">
                      <input
                        type="file"
                        className="form-control"
                        accept=".txt"
                        onChange={handleFileChange}
                      />
                    </div>
                  )}
                  <div className="mb-3">
                    <select
                      className="form-select"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                    >
                      <option value="Logistic Regression">Logistic Regression</option>
                      <option value="XGBoost">XGBoost</option>
                      <option value="Support Vector Machine">Support Vector Machine</option>
                      <option value="Gradient Boosting Machine">Gradient Boosting Machine</option>
                    </select>
                  </div>
                  <button className="btn btn-primary w-100" type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        {offline
                          ? 'Connection lost, reconnecting...'
                          : waiting
                          ? 'Waiting in queue...'
                          : uploading
                            ? `Uploading file... ${uploadPercent}%`
                            : progress && progress.total
                            ? `Predicting... ${progress.done.toLocaleString()} / ${progress.total.toLocaleString()}`
                            : 'Predicting...'}
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                  {progress && progress.total > 0 && !waiting && (
                    <div className="progress mt-3">
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${Math.round((progress.done / progress.total) * 100)}%`, backgroundColor: '#1e4d7a' }}
                      >
                        {Math.round((progress.done / progress.total) * 100)}%
                      </div>
                    </div>
                  )}
                  {queueMessage && (
                    <div className="alert alert-info mt-3 mb-0">{queueMessage}</div>
                  )}
                  {loading && jobNumber && (
                    <p className="text-muted mt-2 mb-0">
                      {waiting ? `Job ${jobNumber} is waiting for its turn.` : uploading ? `Sending the file for job ${jobNumber}.` : `Job ${jobNumber} is running.`}
                    </p>
                  )}
                  {loading && jobNumber && (
                    <button type="button" className="btn btn-outline-danger w-100 mt-3" onClick={handleStop}>
                      Stop job
                    </button>
                  )}
                  {offline && (
                    <div className="alert alert-danger mt-3 mb-0">
                      Connection lost. This tab keeps trying to reconnect and your job is kept for up to 10 minutes. If the connection does not return in time, the job is terminated and no results are saved.
                    </div>
                  )}
                  {loading && jobNumber && (
                    <div className="alert alert-warning mt-3 mb-0">
                      <strong>Keep this tab open until your results are returned.</strong> Closing the tab, leaving the Predict page or losing your connection for more than 10 minutes will terminate your job, and nothing is saved on the server. Use <em>Stop job</em> to cancel it yourself.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
        {notice && <div className="alert alert-info mt-3 text-center">{notice}</div>}
        {error && <div className="alert alert-danger mt-3 text-center">{error}</div>}
      </div>
    </>
  );
}


// Tutorial Page
function Tutorial() {
  return (
    <>
      <Helmet>
        <title>DNMT1pred | Tutorial</title>
        <meta name="description" content="Step-by-step guide on how to use DNMT1pred for single and bulk predictions of DNMT1 inhibitors." />
      </Helmet>
      <div className="container mt-4">
        <header className="d-flex justify-content-between align-items-center">
          <h1>DNMT1pred</h1>
          <Nav />
        </header>
        <div className="mt-4" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <p>This tutorial walks you through how to use DNMT1pred to predict the activity of chemical compounds against DNMT1.</p>
          <h4 className="mt-4">1. Home Page</h4>
          <p>The home page gives an overview of the app and its models. Click <strong>Predict</strong> to get started.</p>
          <img src={s1} alt="Home page" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <h4 className="mt-4">2. Single Molecule Prediction (Logistic Regression)</h4>
          <p>Enter a Molecule ID (optional) and a SMILES string, select <strong>Logistic Regression</strong> and click Submit.</p>
          <img src={s1_b} alt="Single SMILES filled" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <img src={s1_bb} alt="LR result" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <h4 className="mt-4">3. Single Molecule Prediction (XGBoost)</h4>
          <p>Enter a Molecule ID (optional) and a SMILES string, select <strong>XGBoost</strong> and click Submit.</p>
          <img src={s1_c} alt="XGBoost input" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <img src={s1_cc} alt="XGBoost result" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <h4 className="mt-4">4. Single Molecule Prediction (Support Vector Machine)</h4>
          <p>Enter a Molecule ID (optional) and a SMILES string, select <strong>Support Vector Machine</strong> and click Submit.</p>
          <img src={s1_d} alt="SVM input" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <img src={s1_dd} alt="SVM result" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <h4 className="mt-4">5. Single Molecule Prediction (Gradient Boosting Machine)</h4>
          <p>Enter a Molecule ID (optional) and a SMILES string, select <strong>Gradient Boosting Machine</strong> and click Submit.</p>
          <img src={s1_e} alt="GBM input" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <img src={s1_ee} alt="GBM result" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <h4 className="mt-4">6. Bulk Prediction (Logistic Regression)</h4>
          <p>Switch to the <strong>Upload Bulk File</strong> tab, download the sample file to see the expected format, then upload your own <code>.txt</code> file with ID and SMILES columns. Select <strong>Logistic Regression</strong> and click Submit. You can optionally download the prediction results as a CSV file</p>
          <img src={s2_b} alt="Bulk LR upload" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <img src={s2_bb} alt="Bulk LR results table" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <h4 className="mt-4">7. Bulk Prediction (XGBoost)</h4>
          <p>Switch to the <strong>Upload Bulk File</strong> tab, download the sample file to see the expected format, then upload your own <code>.txt</code> file with ID and SMILES columns. Select <strong>XGBoost</strong> and click Submit. You can optionally download the prediction results as a CSV file</p>
          <img src={s2_c} alt="Bulk XGBoost upload" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <img src={s2_cc} alt="Bulk XGBoost results" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <h4 className="mt-4">8. Bulk Prediction (Support Vector Machine)</h4>
          <p>Switch to the <strong>Upload Bulk File</strong> tab, download the sample file to see the expected format, then upload your own <code>.txt</code> file with ID and SMILES columns. Select <strong>Support Vector Machine</strong> and click Submit. You can optionally download the prediction results as a csv file</p>
          <img src={s2_d} alt="Bulk SVM upload" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <img src={s2_dd} alt="Bulk SVM results" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <h4 className="mt-4">9. Bulk Prediction (Gradient Boosting Machine)</h4>
          <p>Switch to the <strong>Upload Bulk File</strong> tab, download the sample file to see the expected format, then upload your own <code>.txt</code> file with ID and SMILES columns. Select <strong>Gradient Boosting Machine</strong> and click Submit. You can optionally download the prediction results as a CSV file</p>
          <img src={s2_e} alt="Bulk GBM upload" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <img src={s2_ee} alt="Bulk GBM results" className="img-fluid mb-2 border rounded" style={{ maxWidth: '100%' }} />
          <h4 className="mt-4">Bulk Jobs, Queueing and Keeping Your Tab Open</h4>
          <p>Only one bulk job is processed on the server at a time. If the server is busy, your job waits in a queue and your file is only uploaded when its turn comes, so please keep your tab open. Each browser tab shows only its own job.</p>
          <p><strong>Keep the tab open until your results are returned.</strong> Closing the tab or leaving the Predict page terminates your job, and nothing is saved on the server. If your internet connection drops, the tab keeps trying to reconnect and your job is kept for up to 10 minutes; after that it is terminated. A job that is already running also survives a page refresh, but a job still waiting in the queue does not, because your file cannot be kept by the browser. You can cancel a job yourself at any time with the <strong>Stop job</strong> button. When the results appear, download the ZIP file straight away, because the results are lost if the tab is closed or the connection drops.</p>
          <h4 className="mt-4">Understanding the Results</h4>
          <p><strong>Activity:</strong> 'Active' denotes the molecule under investigation is a potential DNMT1 inhibitor; 'Inactive' denotes the molecule under investigation is not a potential inhibitor of DNMT1; 'Inconclusive' means the molecule under investigation is likely to be predicted with high uncertainty, so its confidence level and AD status are reported as N/A.</p>
          <p><strong>Confidence Level:</strong> Probability score from 0 to 1. A higher score close to 1 means more confident whilst scores closer to 0 represents less confident predictions.</p>
          <p><strong>AD (Applicability Domain):</strong> Molecule predicted Within the AD means that molecule is similar to the training dataset chemical distribution or features while Outside means the prediction may be less reliable.</p>
          <p><strong>Ro5 (Lipinski's Rule of Five):</strong> Pass means the compound has drug-like properties defined by Ro5, which specifically applies to orally administered drugs.</p>
        </div>
      </div>
    </>
  );
}

// FAQs Page
function FAQs() {
  return (
    <>
      <Helmet>
        <title>DNMT1pred | Frequently Asked Questions</title>
        <meta name="description" content="Answers to common questions about DNMT1pred, models, input formats, and results interpretation." />
      </Helmet>
      <div className="container mt-4">
        <header className="d-flex justify-content-between align-items-center">
          <h1>DNMT1pred</h1>
          <Nav />
        </header>
        <div className="mt-4" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h5 className="mt-4">1. What is DNMT1pred?</h5>
          <p>DNMT1pred is a web-based machine learning application that predicts the bioactivity of chemical compounds against DNA Methyltransferase 1 (DNMT1), a key enzyme involved in epigenetic regulation and a target for cancer therapy.</p>
          <h5 className="mt-4">2. What input format does the app accept?</h5>
          <p>The app accepts SMILES (Simplified Molecular Input Line Entry System) strings, which are standard text representations of chemical structures. For bulk predictions, upload a tab-separated <code>.txt</code> file with <strong>ID</strong> and <strong>SMILES</strong> as column headers.</p>
          <h5 className="mt-4">3. What do the activity labels mean?</h5>
          <p><strong>Active</strong> means the compound is predicted to inhibit DNMT1. <strong>Inactive</strong> means it is predicted not to inhibit DNMT1. <strong>Inconclusive</strong> means the model's confidence is too low to make a reliable prediction thus, the compound falls near the decision boundary. Its confidence level and AD are reported as N/A.</p>
          <h5 className="mt-4">4. What is the Confidence Level?</h5>
          <p>The Confidence Level is the predicted probability score (0 to 1) associated with the assigned activity label. A score closer to 1 indicates higher model confidence in the prediction.</p>
          <h5 className="mt-4">5. What is the Applicability Domain (AD)?</h5>
          <p>The Applicability Domain defines the chemical space covered by the training data. <strong>Within</strong> means the compound is structurally similar to the training set and the prediction is more reliable. <strong>Outside</strong> means the compound is structurally dissimilar and the prediction should be interpreted with caution. The AD plot is graphically provided for only single molecule predictions.</p>
          <h5 className="mt-4">6. What is Lipinski's Rule of Five (Ro5)?</h5>
          <p>Ro5 is a set of rules used to evaluate drug-likeness. A compound <strong>passes</strong> if it has no more than one violation of: molecular weight (MW) ≤ 500 Da, solubility (MolLogP) ≤ 5, hydrogen bond donors (#HBD) ≤ 5, and hydrogen bond acceptors (#HBA) ≤ 10. Failing Ro5 does not disqualify a compound as a potential drug candidate but suggests it may have poor oral bioavailability.</p>
          <h5 className="mt-4">7. Which model should I use?</h5>
          <p>All four models have been rigorously tested and validated. All models achieved competitively high performance on both the testing and validation datasets, all with observed accuracy of 91% on the testing dataset however, Logistic Regression and Gradient Boosting Machines were marginally robust and made high confident predictions from within their AD compared to Support Vector Machines and XGBoost. We recommend running predictions with all models and comparing results, especially for compounds flagged as borderline or Inconclusive by one or more models.</p>
          <h5 className="mt-4">8. How large a file can I upload for bulk predictions?</h5>
          <p>The maximum file size is <strong>100MB</strong>. If your file exceeds this limit, split it into smaller chunks of no more than 100MB each and submit them separately.</p>
          <h5 className="mt-4">9. Can I download my prediction results?</h5>
          <p>Yes. For bulk predictions, a <strong>Download Full Results (ZIP)</strong> button is provided on the results page. The ZIP contains a CSV file with the molecule ID, SMILES, model used, molecular weight, activity, confidence level, AD status, and Ro5 summary. Please download it as soon as the results appear: results are not stored, and closing the tab or losing your connection means they are lost.</p>
          <h5 className="mt-4">What happens if I close the tab or lose my connection during a bulk job?</h5>
          <p>If the connection returns within 10 minutes, the tab reconnects and the job carries on. After 10 minutes, or if you close the tab, the job is terminated and no results are kept. Keep the tab open until the results page appears, and use the Stop job button if you want to cancel. If the server is busy, your job waits in a queue and your file is only uploaded when its turn comes.</p>
          <h5 className="mt-4">10. How were the models trained and validated?</h5>
          <p>The models were trained on 3,371 compounds, tested on 756 compounds, and validated on 85 compounds, all sourced from PubChem bioactivity data for DNMT1. Molecular features were generated using MACCS keys fingerprints and RDKit molecular descriptors, followed by chi-square feature selection, standard scaling and application of an adapted linear regression filter.</p>
        </div>
      </div>
    </>
  );
}

// Contact Page
function Contact() {
  return (
    <>
      <Helmet>
        <title>DNMT1pred | Contact Us</title>
        <meta name="description" content="Contact the developers of DNMT1pred at University of Ghana - Biomedical Engineering Department." />
      </Helmet>
      <div className="container mt-4">
        <header className="d-flex justify-content-between align-items-center">
          <h1>DNMT1pred</h1>
          <Nav />
        </header>
        <div className="mt-4" style={{ maxWidth: '700px', margin: '0 auto' }}>
          <p>For questions, feedback, or collaboration inquiries regarding DNMT1pred, please reach out using the details below.</p>
          <h5 className="mt-4">Developers</h5>
          <p><strong>Principal Developer:</strong> Yvonne Kafui Konku-Asase</p>
          <p>Email: <a href="mailto:yvonnekafui@gmail.com">yvonnekafui@gmail.com</a></p>
          <p><strong>Assistant Developer:</strong> Gideon Kwame Darko</p>
          <p>Email: <a href="mailto:darkogid789@gmail.com">darkogid789@gmail.com</a></p>
          <p><strong>Institution:</strong> University of Ghana</p>
          <p><strong>Department:</strong> Department of Biomedical Engineering</p>
          <p><strong>Department Website:</strong> <a href="https://www.ug.edu.gh/biomedical-engineering/home" target="_blank" rel="noreferrer">www.ug.edu.gh/biomedical-engineering/home</a></p>
          <h5 className="mt-4">Investigators</h5>
          <p><strong>Principal Investigator:</strong> Prof. Samuel Kojo Kwofie</p>
          <p>Email: <a href="mailto:skkwofie@ug.edu.gh">skkwofie@ug.edu.gh</a></p>
          <p><strong>Assistant Investigator:</strong> Dr. Claude Fiifi Hayford</p>
          <p>Email: <a href="mailto:chayford@ug.edu.gh">chayford@ug.edu.gh</a></p>
          <h5 className="mt-4">Citation</h5>
          <p>If you use DNMT1pred in your research, please cite:</p>
          <div className="p-3 border rounded" style={{ backgroundColor: '#fff', fontSize: '0.9rem' }}>
            <p className="mb-0">[We will update the journal details once the paper is published]</p>
          </div>
        </div>
      </div>
    </>
  );
}

// Activity badge helper
function ActivityBadge({ activity }) {
  const cls =
    activity === 'Active' ? 'activity-active' :
    activity === 'Inactive' ? 'activity-inactive' :
    (activity === 'Inconclusive' || activity === 'N/A') ? 'activity-inconclusive' : '';
  return <span className={cls}>{activity || '-'}</span>;
}

// Summary counts helper for bulk results
function SummaryGroup({ title, items, total }) {
  return (
    <div className="mb-3">
      <p className="visual-label text-center">{title}</p>
      <div className="d-flex flex-wrap justify-content-center gap-3">
        {items.map(([label, value]) => (
          <div key={label} className="card px-4 py-2 text-center">
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1e4d7a' }}>{value.toLocaleString()}</div>
            <div>{label}{total > 0 ? ` (${((value / total) * 100).toFixed(1)}%)` : ''}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Results Page
function Results() {
  const { state } = useLocation();
  const [downloadError, setDownloadError] = useState('');
  const [downloaded, setDownloaded] = useState(false);
  const bulkJob = state && state.bulk ? state.bulk : null;
  const result = state && !state.bulk ? state : null;
  const bulkResults = bulkJob ? bulkJob.preview : null;

  // Warn before the tab is closed while the bulk results have not been downloaded yet
  useEffect(() => {
    if (!bulkJob || downloaded) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [bulkJob, downloaded]);

  // Keep the server awake and the results file available while this page is open
  useEffect(() => {
    if (!bulkJob) return undefined;
    const ping = () => { axios.get(`${API_BASE}/healthz`).catch(() => {}); };
    const timer = setInterval(ping, 2 * 60 * 1000);
    return () => clearInterval(timer);
  }, [bulkJob]);

  // Download a file from the API and save it under the given name
  const downloadFile = async (url, filename) => {
    setDownloadError('');
    try {
      const r = await axios.post(url, null, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(r.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      setDownloaded(true);
    } catch (err) {
      console.error('Download error:', err);
      setDownloadError(`Could not download the file (${err.message}). The job may have expired, please resubmit your file.`);
    }
  };

  return (
    <>
      <Helmet>
        <title>DNMT1pred | Prediction Results</title>
        <meta name="description" content="View your DNMT1 inhibitor prediction results including activity, confidence, and applicability domain." />
      </Helmet>
      <div className="container mt-4">
        <header className="d-flex justify-content-between align-items-center">
          <h1>DNMT1pred</h1>
          <Nav />
        </header>
        <div className="mt-4">
          {!result && !bulkJob && (
            <p>No results to display. Please go to the <Link to="/predict">Predict</Link> page to make a prediction.</p>
          )}
          {(result || bulkJob) && (
            <div className="alert alert-warning text-center">
              <strong>Important:</strong> your results are not kept permanently. {bulkJob ? 'Download the full results as a ZIP file now: ' : ''}closing this tab, a server restart or waiting more than about 6 hours will permanently lose these results.
            </div>
          )}
          {/* ===== SINGLE PREDICTION ===== */}
          {result && (
            <div className="single-prediction-results">
              <p className="smiles-display">
                <strong>SMILES:</strong> {result.smiles}
              </p>
              {/* Side-by-side: 1 part Molecule + 2 parts Williams Plot */}
              <div className="results-visual-row">
                {/* Left Column (1/3 width) */}
                <div className="results-mol-outer">
                  <p className="visual-label">2D Molecular Structure</p>
                  <div className="results-mol-image">
                    <img
                      src={`data:image/png;base64,${result.structure_image}`}
                      alt="Molecular Structure"
                    />
                  </div>
                </div>
                {/* Right Column (2/3 width) */}
                {result.ad_plot && (
                  <div className="results-ad-plot">
                    <p className="visual-label">Applicability Domain (Williams Plot)</p>
                    <div className="results-ad-plot-container">
                      <img
                        src={`data:image/png;base64,${result.ad_plot}`}
                        alt="Applicability Domain Williams Plot"
                      />
                    </div>
                  </div>
                )}
              </div>
              {/* Results table */}
              <div className="results-table-wrapper mt-4">
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>SMILES</th>
                        <th>Model</th>
                        <th>MW(g/mol)</th>
                        <th>Activity</th>
                        <th>Confidence Level</th>
                        <th>AD</th>
                        <th>Ro5</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{result.cid || '-'}</td>
                        <td>{result.smiles}</td>
                        <td>{result.model_used}</td>
                        <td>{typeof result.molecular_weight === "number" ? result.molecular_weight.toFixed(2) : '-'}</td>
                        <td><ActivityBadge activity={result.activity} /></td>
                        <td>{typeof result.probability === "number" ? result.probability.toFixed(2) : result.probability || '-'}</td>
                        <td>{result.ad_analysis || '-'}</td>
                        <td>{result.ro5_summary || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {/* ===== BULK PREDICTION ===== */}
          {bulkJob && (
            <div>
              <p className="bulk-count">
                Processed <strong>{bulkJob.summary.processed.toLocaleString()}</strong> molecules using <strong>{bulkJob.model_used}</strong>.
              </p>
              {bulkJob.summary.errors > 0 && (
                <p className="bulk-count">
                  <strong>{bulkJob.summary.errors.toLocaleString()}</strong> rows could not be processed (invalid SMILES or line format) and are marked as errors in the downloadable results file.
                </p>
              )}
              <SummaryGroup
                title="Predicted Activity"
                total={bulkJob.summary.predicted}
                items={[
                  ['Active', bulkJob.summary.active],
                  ['Inactive', bulkJob.summary.inactive],
                  ['Inconclusive (uncertain)', bulkJob.summary.inconclusive],
                ]}
              />
              <div className="d-flex justify-content-center mb-2 mt-4">
                <button className="btn btn-success" onClick={() => downloadFile(bulkJob.download_url, `DNMT1pred_results_${bulkJob.model_used.replace(/[^A-Za-z0-9]+/g, '_')}.zip`)}>
                  Download Full Results (ZIP)
                </button>
              </div>
              {downloadError && <div className="alert alert-danger text-center mb-4">{downloadError}</div>}
              <p className="text-center text-muted mt-3">
                <strong>NOTE:</strong> The Applicability Domain (AD) status of every molecule (Within or Outside) can be inferred from the AD column of the CSV file inside the downloaded ZIP.
              </p>
              <p className="bulk-count text-center">
                Showing the first {bulkResults.length.toLocaleString()} of {bulkJob.summary.processed.toLocaleString()} rows. Download the ZIP for all results.
              </p>
              <div className="results-table-wrapper">
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>SMILES</th>
                        <th>Model</th>
                        <th>MW(g/mol)</th>
                        <th>Activity</th>
                        <th>Confidence Level</th>
                        <th>AD</th>
                        <th>Ro5</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResults.map((item, index) => (
                        <tr key={index}>
                          <td>{item.cid || '-'}</td>
                          <td>{item.smiles || '-'}</td>
                          {item.error ? (
                            <td colSpan={6} style={{ color: '#721c24' }}>{item.error}</td>
                          ) : (
                            <>
                              <td>{item.model_used || '-'}</td>
                              <td>{typeof item.molecular_weight === "number" ? item.molecular_weight.toFixed(2) : '-'}</td>
                              <td><ActivityBadge activity={item.activity} /></td>
                              <td>{typeof item.probability === "number" ? item.probability.toFixed(2) : item.probability || '-'}</td>
                              <td>{item.ad_analysis || '-'}</td>
                              <td>{item.ro5_summary || '-'}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Main App Component with Routing
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/predict" element={<Predict />} />
        <Route path="/tutorial" element={<Tutorial />} />
        <Route path="/faqs" element={<FAQs />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/results" element={<Results />} />
      </Routes>
    </Router>
  );
}

export default App;
